import axios from 'axios';

/**
 * Error for non-axios (fetch-based) API calls that preserves the HTTP status
 * so the global 403 policy and retry rules can classify it.
 */
export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * An error whose message is written for the operator and can be shown as-is
 * by the global mutation error snackbar (which otherwise falls back to a
 * generic "Something went wrong" for non-HTTP failures). Throw it from a
 * mutation when the server accepted a write but the outcome is not what the
 * user asked for.
 */
export class UserFacingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

export function getHttpStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  if (error instanceof HttpError) {
    return error.status;
  }
  return undefined;
}

export function isForbiddenError(error: unknown): boolean {
  return getHttpStatus(error) === 403;
}

export function isNotFoundError(error: unknown): boolean {
  return getHttpStatus(error) === 404;
}

export function isAuthError(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status === 401 || status === 403;
}

/**
 * Builds a React Query retry predicate that never retries 401/403 (they
 * cannot succeed on retry and delay the restricted state) while retrying
 * other failures up to maxFailures times.
 */
export function createQueryRetryPredicate(options: {
  maxFailures: number;
  skipStatuses?: number[];
}): (failureCount: number, error: unknown) => boolean {
  const { maxFailures, skipStatuses = [] } = options;
  return (failureCount, error) => {
    if (isAuthError(error)) {
      return false;
    }
    const status = getHttpStatus(error);
    if (status !== undefined && skipStatuses.includes(status)) {
      return false;
    }
    return failureCount < maxFailures;
  };
}
